import { toast } from "@/hooks/use-toast";

interface BriefData {
  message: string;
  patternLabel: string;
  manipulationScore: number;
  toxicityScore: number;
  confidence: "high" | "moderate" | "low" | string;
  signals: string[];
  shortExplanation: string;
  nuance: string;
  savedResponse?: string | null;
}

const sevLabel = (v: number, type: "manip" | "harm") => {
  if (v >= 7) return "CRITICAL";
  if (v >= 5.5) return type === "manip" ? "AMBIGUOUS / MODERATE" : "MODERATE";
  if (v >= 4) return "LOW-MODERATE";
  return "LOW";
};
const sevRGB = (v: number): [number, number, number] =>
  v >= 7 ? [239, 68, 68] : v >= 5.5 ? [245, 158, 11] : v >= 4 ? [180, 160, 100] : [100, 116, 139];

export async function exportPdfBrief(d: BriefData) {
  try {
    const { default: jsPDF } = await import("jspdf");
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 48;
    const CW = W - M * 2;
    let y = 0;

    doc.setFillColor(12, 12, 12);
    doc.rect(0, 0, W, H, "F");
    doc.setFillColor(0, 242, 255);
    doc.rect(0, 0, W, 3, "F");

    y = 40;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(0, 242, 255);
    doc.text("GREYROCK AI", M, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text("FORENSIC BRIEF", W - M, y, { align: "right" });

    y += 14;
    doc.setDrawColor(40, 40, 40);
    doc.line(M, y, W - M, y);

    y += 26;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(235, 235, 235);
    doc.text(d.patternLabel.toUpperCase(), M, y);

    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(140, 140, 140);
    doc.text(`Confidence: ${String(d.confidence).toUpperCase()}`, M, y);

    // ── Analyzed message
    y += 28;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(0, 242, 255);
    doc.text("ANALYZED MESSAGE", M, y);
    y += 12;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(11);
    doc.setTextColor(220, 220, 220);
    const msgLines = doc.splitTextToSize(`"${d.message}"`, CW);
    doc.text(msgLines, M, y);
    y += msgLines.length * 14 + 14;

    // ── Scores
    const drawScore = (x: number, label: string, value: number, type: "manip" | "harm") => {
      const [r, g, b] = sevRGB(value);
      doc.setFillColor(20, 20, 20);
      doc.roundedRect(x, y, (CW - 12) / 2, 70, 6, 6, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(140, 140, 140);
      doc.text(label, x + 14, y + 18);
      doc.setFontSize(28);
      doc.setTextColor(r, g, b);
      doc.text(value.toFixed(1), x + 14, y + 48);
      doc.setFontSize(8);
      doc.setTextColor(180, 180, 180);
      doc.text(sevLabel(value, type), x + 14, y + 62);
    };
    drawScore(M, "MANIPULATION INDEX", d.manipulationScore, "manip");
    drawScore(M + (CW - 12) / 2 + 12, "EMOTIONAL HARM", d.toxicityScore, "harm");
    y += 86;

    // ── Tactical interpretation
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(0, 242, 255);
    doc.text("TACTICAL INTERPRETATION", M, y);
    y += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(220, 220, 220);
    const expLines = doc.splitTextToSize(d.shortExplanation, CW);
    doc.text(expLines, M, y);
    y += expLines.length * 13 + 14;

    // ── Nuance
    if (d.nuance) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0, 242, 255);
      doc.text("NUANCE ASSESSMENT", M, y);
      y += 12;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(200, 200, 200);
      const nLines = doc.splitTextToSize(d.nuance, CW);
      doc.text(nLines, M, y);
      y += nLines.length * 13 + 14;
    }

    // ── Signals
    if (d.signals?.length) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0, 242, 255);
      doc.text("DETECTED SIGNALS", M, y);
      y += 12;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(220, 220, 220);
      doc.text(d.signals.join(" · "), M, y);
      y += 18;
    }

    // ── Saved response
    if (d.savedResponse) {
      y += 6;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0, 242, 255);
      doc.text("DEPLOYED RESPONSE", M, y);
      y += 12;
      doc.setFillColor(18, 22, 24);
      doc.setDrawColor(0, 80, 90);
      const rLines = doc.splitTextToSize(`"${d.savedResponse}"`, CW - 24);
      const boxH = rLines.length * 13 + 24;
      doc.roundedRect(M, y, CW, boxH, 4, 4, "FD");
      doc.setFont("helvetica", "italic");
      doc.setFontSize(10);
      doc.setTextColor(220, 235, 240);
      doc.text(rLines, M + 12, y + 16);
      y += boxH + 10;
    }

    // ── Footer
    doc.setDrawColor(40, 40, 40);
    doc.line(M, H - 50, W - M, H - 50);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(110, 110, 110);
    doc.text(`GENERATED ${new Date().toISOString()}`, M, H - 36);
    doc.text("greyrockai.app · Forensic Behavioral Intelligence", W - M, H - 36, { align: "right" });

    doc.save("GreyRockAI-Forensic-Brief.pdf");
    toast({ title: "PDF Brief downloaded" });
  } catch (e) {
    console.error(e);
    toast({ title: "Export failed", variant: "destructive" });
  }
}

export function exportTextFile(content: string, filename: string) {
  try {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded", description: filename });
  } catch {
    toast({ title: "Export failed", variant: "destructive" });
  }
}

export function buildTacticalSummary(d: {
  message: string;
  patternLabel: string;
  score: number;
  manipulationScore: number;
  toxicityScore: number;
  confidence: string;
  signals: string[];
  shortExplanation: string;
  nuance: string;
  savedResponse?: string | null;
}) {
  return [
    "GREYROCK AI — TACTICAL SUMMARY",
    "═══════════════════════════════════════════",
    `GENERATED: ${new Date().toISOString()}`,
    "",
    "ANALYZED MESSAGE",
    "───────────────────────────────────────────",
    `"${d.message}"`,
    "",
    "FORENSIC ASSESSMENT",
    "───────────────────────────────────────────",
    `Primary Pattern:      ${d.patternLabel}`,
    `Threat Level:         ${d.score.toFixed(1)} / 10`,
    `Manipulation Index:   ${d.manipulationScore.toFixed(1)} / 10`,
    `Emotional Harm:       ${d.toxicityScore.toFixed(1)} / 10`,
    `Confidence:           ${String(d.confidence).toUpperCase()}`,
    "",
    "DETECTED SIGNALS",
    "───────────────────────────────────────────",
    d.signals.map((s) => `  • ${s}`).join("\n"),
    "",
    "TACTICAL INTERPRETATION",
    "───────────────────────────────────────────",
    d.shortExplanation,
    "",
    ...(d.nuance ? ["NUANCE ASSESSMENT", "───────────────────────────────────────────", d.nuance, ""] : []),
    ...(d.savedResponse ? ["DEPLOYED RESPONSE", "───────────────────────────────────────────", `"${d.savedResponse}"`, ""] : []),
    "═══════════════════════════════════════════",
    "greyrockai.app · Forensic Behavioral Intelligence",
  ].join("\n");
}

export function buildConversationSnapshot(d: {
  message: string;
  patternLabel: string;
  score: number;
  timestamp?: string | null;
}) {
  const ts = d.timestamp ? new Date(d.timestamp).toISOString() : new Date().toISOString();
  return [
    "GREYROCK AI — CONVERSATION SNAPSHOT",
    "═══════════════════════════════════════════",
    `CAPTURED:   ${ts}`,
    `PATTERN:    ${d.patternLabel}`,
    `SCORE:      ${d.score.toFixed(1)} / 10`,
    "═══════════════════════════════════════════",
    "",
    "VERBATIM MESSAGE",
    "───────────────────────────────────────────",
    d.message,
    "",
    "═══════════════════════════════════════════",
    "Preserve as-is for documentation, legal,",
    "or therapeutic reference.",
    "greyrockai.app",
  ].join("\n");
}
